"use client";

/**
 * CtaClickDelegate — ONE capture-phase click listener for the whole page.
 *
 * Any click on a button / link / role=button inside a tracked section (a
 * [data-track="on"] wrapper produced by TrackedWidget) fires a sheet-aligned
 * cta_click, attributed to that section's widget_name / widget_order — with NO
 * per-widget code. Mounted once by NewPage.
 *
 * Why capture phase: it runs BEFORE any child's bubble-phase handler, so a
 * widget that calls stopPropagation() in its own onClick cannot swallow the
 * event here (only ~7 widgets do, and this covers them).
 *
 * Scope / precision:
 *   - Only clicks whose nearest [data-track] is "on" fire. data-track="off"
 *     (admin per-widget toggle) and clicks with no tracked ancestor (page chrome,
 *     portal-rendered lead drawers) are ignored — those have their own events.
 *   - cta_name: `data-cta-name` on the element wins; otherwise the slugified
 *     visible label (aria-label preferred for icon-only controls).
 *   - `data-track-ignore` on the element or any ancestor suppresses that click
 *     (e.g. carousel arrows, close buttons you don't want as cta_click).
 *
 * Part of the decoupled analytics feature — delete alongside rudderStackEvents.ts.
 */

import { useEffect } from "react";

import { trackCtaClick } from "@/customAnalytics/rudderStackEvents";

const CTA_SELECTOR = "a, button, [role='button'], input[type='submit'], [data-cta]";

/** snake_case slug from a human label; bounded so a stray long label can't bloat. */
const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

export default function CtaClickDelegate() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || typeof target.closest !== "function") return;

      const el = target.closest<HTMLElement>(CTA_SELECTOR);
      if (!el) return;
      if (el.closest("[data-track-ignore]")) return;

      const scope = el.closest<HTMLElement>("[data-track]");
      if (!scope || scope.dataset.track !== "on") return;

      const text = (el.getAttribute("aria-label") || el.textContent || "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 80);

      const ctaName = el.dataset.ctaName || slugify(text);
      if (!ctaName) return; // icon-only, no label, no explicit name → nothing to attribute

      trackCtaClick({
        widget_name: scope.dataset.widgetName || "",
        widget_order: scope.dataset.widgetOrder || "",
        cta_name: ctaName,
        cta_text: text,
      });
    };

    // Capture phase (3rd arg true) on document so it sits above React's root and
    // fires regardless of child stopPropagation. Passive read only — never blocks.
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}
